import { useEffect, useState } from "react";
import { Loader2, Plus, ShieldCheck, CheckCircle, Ban } from "lucide-react";
import {
    adminListWarrantyCompanies,
    adminCreateWarrantyCompany,
    adminUpdateWarrantyStatus,
} from "@/Calls/callWarrantyAuth";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";

interface Company {
    company_id: number;
    company_name: string;
    company_code: string;
    company_type: string;
    email: string;
    phone: string | null;
    status: string;
    leads_total: number;
    balance: number;
    billing_invoice_no: number | null;
    vehicle_types_covered: string[] | null;
    lead_rate: string;
}

function StatusBadge({ status }: { status: string }) {
    if (status === "ACTIVE")    return <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Active</Badge>;
    if (status === "SUSPENDED") return <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Suspended</Badge>;
    return <Badge variant="outline">Pending</Badge>;
}

const INITIAL_FORM = {
    company_name: "", company_code: "", email: "", password: "",
    phone: "", contact_name: "", company_type: "STANDARD",
    license_states: "", license_number: "", vehicle_types_covered: [] as string[],
    lead_rate: "25.00", website: "", notes: "",
};

export default function AdminWarrantyPage() {
    const [companies,  setCompanies]  = useState<Company[]>([]);
    const [loading,    setLoading]    = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const [form,       setForm]       = useState(INITIAL_FORM);
    const [submitting, setSubmitting] = useState(false);
    const [formError,  setFormError]  = useState<string | null>(null);

    const load = () => {
        setLoading(true);
        adminListWarrantyCompanies()
            .then((d) => setCompanies(d.companies ?? []))
            .catch(console.error)
            .finally(() => setLoading(false));
    };

    useEffect(load, []);

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true); setFormError(null);
        try {
            const res = await adminCreateWarrantyCompany({
                ...form,
                vehicle_types_covered: form.vehicle_types_covered.length > 0 ? form.vehicle_types_covered : undefined,
            });
            if (!res.ok) throw new Error(res.message);
            setShowCreate(false); setForm(INITIAL_FORM); load();
        } catch (e: any) { setFormError(e.message); }
        finally { setSubmitting(false); }
    };

    const handleStatus = async (id: number, status: string) => {
        await adminUpdateWarrantyStatus(id, status); load();
    };

    const toggleVehicleType = (type: string) => {
        setForm((prev) => ({
            ...prev,
            vehicle_types_covered: prev.vehicle_types_covered.includes(type)
                ? prev.vehicle_types_covered.filter((t) => t !== type)
                : [...prev.vehicle_types_covered, type],
        }));
    };

    if (loading) return (
        <div className="flex items-center justify-center min-h-64">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
    );

    return (
        <div className="space-y-6 max-w-6xl mx-auto">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-light uppercase tracking-wide">Warranty Companies</h1>
                    <p className="text-muted-foreground text-sm mt-1">{companies.length} registered providers</p>
                </div>
                <Button
                    className="bg-gradient-to-r from-purple-600 to-violet-600 text-white"
                    onClick={() => setShowCreate(true)}
                >
                    <Plus className="h-4 w-4 mr-2" />Add Company
                </Button>
            </div>

            {showCreate && (
                <Card className="p-6 border-2 border-purple-200">
                    <h3 className="font-semibold mb-4">Add Warranty Company</h3>
                    <form onSubmit={handleCreate} className="space-y-4">
                        <div className="grid sm:grid-cols-2 gap-4">
                            <div>
                                <label className="text-sm font-medium">Company Name *</label>
                                <Input required value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="mt-1" />
                            </div>
                            <div>
                                <label className="text-sm font-medium">Company Code *</label>
                                <Input required value={form.company_code} onChange={(e) => setForm({ ...form, company_code: e.target.value })} placeholder="e.g. CARSHIELD" className="mt-1" />
                            </div>
                            <div>
                                <label className="text-sm font-medium">Email *</label>
                                <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" />
                            </div>
                            <div>
                                <label className="text-sm font-medium">Password *</label>
                                <Input required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1" />
                            </div>
                            <div>
                                <label className="text-sm font-medium">Contact Name</label>
                                <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="mt-1" />
                            </div>
                            <div>
                                <label className="text-sm font-medium">Phone</label>
                                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" />
                            </div>
                            <div>
                                <label className="text-sm font-medium">Type</label>
                                <select value={form.company_type} onChange={(e) => setForm({ ...form, company_type: e.target.value })} className="mt-1 block w-full rounded-lg border bg-background px-3 py-2 text-sm">
                                    <option value="MAJOR">Major Provider</option>
                                    <option value="STANDARD">Standard</option>
                                    <option value="SPECIALTY">Specialty</option>
                                </select>
                            </div>
                            <div>
                                <label className="text-sm font-medium">Lead Rate ($)</label>
                                <Input type="number" step="0.01" value={form.lead_rate} onChange={(e) => setForm({ ...form, lead_rate: e.target.value })} className="mt-1" />
                            </div>
                            <div>
                                <label className="text-sm font-medium">License States (comma-separated)</label>
                                <Input value={form.license_states} onChange={(e) => setForm({ ...form, license_states: e.target.value })} placeholder="FL,GA,AL" className="mt-1" />
                            </div>
                            <div>
                                <label className="text-sm font-medium">License Number</label>
                                <Input value={form.license_number} onChange={(e) => setForm({ ...form, license_number: e.target.value })} className="mt-1" />
                            </div>
                        </div>

                        <div>
                            <label className="text-sm font-medium">Vehicle Types (leave empty for ALL)</label>
                            <div className="flex gap-2 mt-1 flex-wrap">
                                {["AUTO", "MOTORCYCLE", "COMMERCIAL"].map((t) => (
                                    <button
                                        key={t} type="button"
                                        onClick={() => toggleVehicleType(t)}
                                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                                            form.vehicle_types_covered.includes(t)
                                                ? "bg-purple-600 text-white border-purple-600"
                                                : "border-border text-muted-foreground hover:border-purple-400"
                                        }`}
                                    >
                                        {t}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {formError && <p className="text-red-500 text-sm">{formError}</p>}

                        <div className="flex gap-3">
                            <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
                            <Button type="submit" className="bg-purple-600 text-white" disabled={submitting}>
                                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                                Create Company
                            </Button>
                        </div>
                    </form>
                </Card>
            )}

            <Card>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="border-b">
                            <tr className="text-left">
                                <th className="p-4 font-medium">Company</th>
                                <th className="p-4 font-medium">Type</th>
                                <th className="p-4 font-medium">Status</th>
                                <th className="p-4 font-medium">Leads Pulled</th>
                                <th className="p-4 font-medium">Balance</th>
                                <th className="p-4 font-medium">Lead Rate</th>
                                <th className="p-4 font-medium">Invoice #</th>
                                <th className="p-4 font-medium">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {companies.length === 0 ? (
                                <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">No warranty companies yet</td></tr>
                            ) : companies.map((c) => (
                                <tr key={c.company_id} className="border-b last:border-0 hover:bg-muted/30">
                                    <td className="p-4">
                                        <div className="flex items-center gap-2">
                                            <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center shrink-0">
                                                <ShieldCheck className="h-4 w-4 text-purple-600" />
                                            </div>
                                            <div>
                                                <p className="font-medium">{c.company_name}</p>
                                                <p className="text-xs text-muted-foreground">{c.email}</p>
                                                {c.vehicle_types_covered && (
                                                    <p className="text-xs text-muted-foreground">{(c.vehicle_types_covered as string[]).join(", ")}</p>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="p-4">
                                        <Badge variant="outline" className="text-xs">{c.company_type}</Badge>
                                    </td>
                                    <td className="p-4"><StatusBadge status={c.status} /></td>
                                    <td className="p-4">{c.leads_total}</td>
                                    <td className="p-4 font-medium">${(c.balance ?? 0).toFixed(2)}</td>
                                    <td className="p-4 text-muted-foreground">${c.lead_rate}</td>
                                    <td className="p-4 text-muted-foreground">{c.billing_invoice_no ?? "—"}</td>
                                    <td className="p-4">
                                        <div className="flex gap-2">
                                            {c.status !== "ACTIVE" && (
                                                <Button size="sm" variant="outline" className="text-green-600" onClick={() => handleStatus(c.company_id, "ACTIVE")}>
                                                    <CheckCircle className="h-3 w-3 mr-1" />Approve
                                                </Button>
                                            )}
                                            {c.status === "ACTIVE" && (
                                                <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleStatus(c.company_id, "SUSPENDED")}>
                                                    <Ban className="h-3 w-3 mr-1" />Suspend
                                                </Button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Card>
        </div>
    );
}
